import React from 'react';
import { Theme, useTheme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { Bar } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { salesChartMode } from 'common/types';
import moment from 'moment-timezone';
import { SalesReportByCurrency } from 'common/api/db/functions/_callable/reports';
import { fillWithLeadingZeros } from 'common/utils/common';

/* Taken from chartjs type definitions. Chart MetaData isn't exported.
 * Properties model, start and view aren't used, and the interfaces are
 * too large to define here.
 */
interface ChartMeta {
	_chart: Chart;
	_datasetIndex: number;
	_index: number;
	_model: any;
	_start?: any;
	_view: any;
	_xScale: Chart.ChartScales;
	_yScale: Chart.ChartScales;
	hidden?: boolean;
}

interface ReportProps {
	dailyReport: SalesReportByCurrency[string]['daily'] | null;
	date: moment.Moment;
	month: moment.Moment;
	viewMode: salesChartMode;
	toggleViewMode: () => void;
	setCurrentDay: (day: moment.Moment) => void;
}

type Props = ReportProps & WithStyles<typeof styles>;

const SalesChart = (props: Props) => {
	const { classes, date, month, viewMode, dailyReport } = props;
	const theme = useTheme();
	const year = date.year();
	const barChartRef = React.createRef<Bar>();

	const getMonthlyChartData = (report: SalesReportByCurrency[string]['daily']) => {
		const chartData = Object.entries(report ?? {}).reduce(
			(tot, [day, dayReport]) => {
				const dayLabel = moment(month).date(Number(day)).format('ddd D');
				const dayData = dayReport.total.revenue / 100;
				return {
					labels: [...tot.labels, dayLabel],
					data: [...tot.data, dayData],
				};
			},
			{ labels: [], data: [] } as { labels: string[]; data: number[] },
		);
		return {
			labels: chartData.labels,
			datasets: [
				{
					data: chartData.data,
					backgroundColor: theme.palette.primary.main,
				},
			],
		};
	};

	const getDailyChartData = (report: SalesReportByCurrency[string]['daily']) => {
		const dayNumber = moment(date).date();
		const chartData = Object.entries(report[dayNumber]?.hourly ?? {}).reduce(
			(tot, [hour, hourlyRevenue]) => {
				const hourLabel = `${fillWithLeadingZeros(hour.toString(), 2)}:00`;
				const revenue = hourlyRevenue / 100;
				return {
					labels: [...tot.labels, hourLabel],
					data: [...tot.data, revenue],
				};
			},
			{ labels: [], data: [] } as { labels: string[]; data: number[] },
		);
		return {
			labels: chartData.labels,
			datasets: [
				{
					data: chartData.data,
					backgroundColor: theme.palette.primary.main,
				},
			],
		};
	};

	const chartData = !dailyReport
		? null
		: viewMode === 'month'
		? getMonthlyChartData(dailyReport)
		: getDailyChartData(dailyReport);

	const handleBarClick = (event: MouseEvent) => {
		if (viewMode === 'day') {
			return;
		}
		const node = barChartRef.current;
		if (node) {
			try {
				const data = node.chartInstance.getElementsAtEvent(event)[0] as ChartMeta;
				const day = data._index + 1;
				const currentYear = year || moment().year();
				const currentMonth = month.month() || moment().month();
				const date = moment().year(currentYear).month(currentMonth).date(day);
				props.setCurrentDay(date);
				props.toggleViewMode();
			} catch (error) {
				// User probably clicked empty area in the chart. No action
			}
		}
	};

	const options: ChartOptions = {
		legend: { display: false },
		scales: {
			xAxes: [
				{
					gridLines: {
						display: false,
					},
				},
			],
			yAxes: [
				{
					ticks: {
						beginAtZero: true,
					},
					gridLines: {
						display: false,
					},
				},
			],
		},
		onClick: handleBarClick,
	};

	const weekSeparatorPlugin = (chart: Chart) => {
		if (viewMode === 'month') {
			const meta = chart.getDatasetMeta(0);
			const ruler = meta.controller._ruler;
			const sunday = moment().isoWeekday(7).format('ddd');
			const barWidth = ruler.scale.width / (ruler.scale.maxIndex + 1);
			const sundayIndices: number[] = ruler.scale.ticks.reduce(
				(a: number[], e: string, i: number) => {
					if (e.startsWith(sunday)) {
						a.push(i);
					}
					return a;
				},
				[],
			);
			const context = chart.ctx!;
			context.save();
			for (const index of sundayIndices) {
				const lineLeftOffset = ruler.scale.left + barWidth * (index + 1);
				context.beginPath();
				context.strokeStyle = 'rgba(0, 0, 0, 0.1)';
				context.moveTo(lineLeftOffset, 0);
				context.lineTo(lineLeftOffset, ruler.scale.top);
				context.stroke();
			}
			context.restore();
		}
	};

	const plugins = [{ afterDatasetDraw: weekSeparatorPlugin }];

	return (
		<div className={classes.chartContainer}>
			<Bar
				ref={barChartRef}
				data={chartData ?? {}}
				options={options}
				height={75}
				plugins={plugins}
			/>
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		spinnerContainer: {
			width: '100%',
			height: 100,
			position: 'relative',
		},
		chartContainer: {
			width: '100%',
		},
	});

export default withStyles(styles)(SalesChart);
